import React, { useCallback, useEffect, useState } from 'react'
import { Box } from '@material-ui/core'
import { useReactiveVar } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { intersectionWith, sum } from 'lodash'
import { Section } from '../../common/Section'
import { EnergyType } from '../../../calculators/EnergyFlow'
import { average, groupIndicesByValue } from '../../../util/array-utility'
import { FormGroup } from '../../form/FormGroup'
import { LineChart } from '../../common/charts/LineChart'
import { hoursInYear } from '../../../util/hours-in-year'
import { ChartHourSeriesSelect } from './ChartHourSeriesSelect'
import { projectVar } from '../../../apollo/reactive-vars'
import { ChartType, ChartTypeToggle } from './ChartTypeToggle'
import {
  formatEnergy,
  formatPower,
  formatTemperature,
} from '../../../util/formatters'
import { ChartProps, EnergyWithName } from './use-chart-util'

export function TemperatureChart({
  titlePrefix,
  chartUtil,
}: ChartProps): React.ReactElement {
  const { t } = useTranslation()
  const { flowOptions, flows, defaultFlows } = chartUtil

  const project = useReactiveVar(projectVar)

  const [selectedFlows, setSelectedFlows] = useState<EnergyWithName[]>([])
  const [flowNames, setFlowNames] = useState<(EnergyType | string)[]>(
    defaultFlows.map((flow) => flow.name),
  )
  const [chartType, setChartType] = useState(ChartType.Power)

  const handleFlowNamesChange = useCallback(
    (values: string[]) => {
      setFlowNames(values)

      const newFlows = intersectionWith(
        flows,
        values,
        (flow, name) => flow.name === name,
      )
      setSelectedFlows(newFlows)
    },
    [flows],
  )

  useEffect(() => {
    handleFlowNamesChange(defaultFlows.map((flow) => flow.name))
  }, [flowOptions, defaultFlows, handleFlowNamesChange])

  useEffect(() => {
    const newFlows = intersectionWith(
      flows,
      flowNames,
      (flow, name) => flow.name === name,
    )
    setSelectedFlows(newFlows)
  }, [flows, flowNames])

  const temperatureHourSeries = project?.temperatureHourSeries
  if (!temperatureHourSeries) {
    return <></>
  }

  // In principle there might be gaps temperature data, that is, there could be e.g. indices for values 26 and 28 with no indices for value 27. However, this should only affect the graph drawing by having a missing point in the data which the graph library should be able to handle.
  const groupedTemperatureIndices = groupIndicesByValue(
    temperatureHourSeries.values,
  )

  const mapFlowToGraphData = (flow: EnergyWithName) => {
    const hourSeries =
      flow.hourSeries.length === 0
        ? (new Array(hoursInYear).fill(0) as number[])
        : flow.hourSeries

    const createGraph = (
      valuesMapper: (values: number[]) => number,
      namePrefix?: string,
    ) => {
      const id = `${namePrefix ? `${namePrefix} ` : ''}${flow.name}`
      const data = Object.entries(groupedTemperatureIndices)
        .map(([key, indices]: [string, number[]]) => {
          const hourSeriesValues = indices.map((index) => hourSeries[index])

          return {
            x: Number(key),
            y: valuesMapper(hourSeriesValues),
          }
        })
        .sort((a, b) => a.x - b.x)
      return { id, data }
    }

    if (chartType === ChartType.Energy) {
      const totalEnergyGraph = createGraph(sum)

      return [totalEnergyGraph]
    }

    const maxPowerGraph = createGraph(
      (values) => Math.max(...values),
      t('charts.legends.max'),
    )
    const minPowerGraph = createGraph(
      (values) => Math.min(...values),
      t('charts.legends.min'),
    )
    const averagePowerGraph = createGraph(average, t('charts.legends.average'))

    return [maxPowerGraph, minPowerGraph, averagePowerGraph]
  }

  const data = selectedFlows.flatMap(mapFlowToGraphData)

  const xAxisValueFormatter = (temperature: number) =>
    temperature % 5 === 0 ? formatTemperature(temperature) : ''

  const valueFormatter =
    chartType === ChartType.Energy ? formatEnergy : formatPower

  return (
    <Section
      title={`${titlePrefix}: ${t('charts.consumption.temperature.title')}`}
    >
      <Box display="flex" flexDirection="row" justifyContent="left">
        <FormGroup>
          <ChartHourSeriesSelect
            values={flowNames}
            onChange={handleFlowNamesChange}
            options={flowOptions}
          />
        </FormGroup>

        <FormGroup ml={2}>
          <ChartTypeToggle value={chartType} onChange={setChartType} />
        </FormGroup>
      </Box>

      <Box width="100%" height={540} borderRadius={6}>
        {data.length > 0 && (
          <LineChart
            data={data}
            xFormatter={formatTemperature}
            yFormatter={valueFormatter}
            xAxisFormatter={xAxisValueFormatter}
          />
        )}
      </Box>
    </Section>
  )
}
